import * as React from 'react';
import { observer } from 'mobx-react';
import { withTheme } from 'styled-components';
import { styled } from '../utils/styled';
import { propStyles } from '../utils/propStyles';
import Tab, { TabProps, TabAccessibility } from './Tab';
import TabRegion from '../models/components/TabRegion';
import { ConditionalDisclaimers } from '../models/configuration/disclaimers/ConditionalDisclaimers';
import { KEYBOARD_KEYS } from '../constants';
import { SuperscriptScope } from '../stores';
import { Theme } from '../models/Theme';

const NAVIGATION_KEYS = [
    KEYBOARD_KEYS.LEFT_ARROW,
    KEYBOARD_KEYS.RIGHT_ARROW,
    KEYBOARD_KEYS.UP_ARROW,
    KEYBOARD_KEYS.DOWN_ARROW,
    KEYBOARD_KEYS.HOME,
    KEYBOARD_KEYS.END,
];

const ACCEPT_KEYS = [
    KEYBOARD_KEYS.ENTER,
    KEYBOARD_KEYS.SPACE,
    KEYBOARD_KEYS.SPACEBAR,
];

const TabsWrapper = styled<any, any>('nav', 'tabsWrapper', 'tabs')`
    ${propStyles};
    top: ${p => (p.top ? `${p.top}` : 'initial')};
`;

TabsWrapper.defaultProps = {
    display: 'flex',
    flex: '1 0 auto',
    flexWrap: 'wrap',
    borderWidth: '1px',
    borderStyle: 'none none solid none',
    borderColor: 'gray300',
    zIndex: 16,
    bgColor: '#fff',
};

export interface TabData {
    id: string;
    label: string;
    disclaimers?: ConditionalDisclaimers;
}

export interface TabsProps {
    tabs: Array<TabData>;
    region: TabRegion;
    setTabActive: (tab: TabData, event?: any) => void;
    isTabSelected: (tabId: string) => boolean;
    getMetricContext: (tab: TabData) => string;
    getAccessibility?: (tab: TabData) => TabAccessibility;
    filterCallback?: (
        tab: TabData,
        index?: number,
        array?: Array<any>
    ) => boolean;
    theme?: Theme;
    // TODO: tabThemeOverride possibly needs to be looked into to get a proper type, the usage expects different types in different places
    tabThemeOverride?: any;
    superscriptScope?: SuperscriptScope;
}

@observer
class Tabs extends React.Component<TabsProps> {
    htmlTabElements: Array<HTMLElement>;

    get workingTabs() {
        const { tabs, filterCallback } = this.props;
        if (tabs) {
            let workingTabs = tabs;
            if (filterCallback) {
                workingTabs = tabs.filter(filterCallback);
            }
            if (!workingTabs || workingTabs.length <= 0) {
                return null;
            }
            return workingTabs;
        }
    }

    constructor(props: TabsProps) {
        super(props);
        this.htmlTabElements = this.initializeHtmlTabElements();
    }

    initializeHtmlTabElements(): Array<HTMLElement> {
        const { workingTabs } = this;
        if (workingTabs && workingTabs.length >= 0) {
            return Array.from({ length: workingTabs.length });
        }
        return [];
    }

    saveTabReference(instance: HTMLElement, tabIndex: number) {
        if (
            this.htmlTabElements &&
            this.htmlTabElements.length - 1 >= tabIndex
        ) {
            this.htmlTabElements[tabIndex] = instance;
        }
    }

    onClick = (e: any, tab: TabData) => {
        const { setTabActive } = this.props;
        if (setTabActive) {
            setTabActive(tab, e);
        }
    };

    onKeyDown = (e: any, tab: TabData, tabIndex: number) => {
        const key = e.key;
        if (NAVIGATION_KEYS.indexOf(key as KEYBOARD_KEYS) !== -1) {
            e.preventDefault();
            e.stopPropagation();
            this.handleKeyboardNavigation(key, tabIndex);
        } else if (ACCEPT_KEYS.indexOf(key as KEYBOARD_KEYS) !== -1) {
            e.preventDefault();
            const { setTabActive } = this.props;
            setTabActive(tab, e);
        }
    };

    handleKeyboardNavigation(key, tabIndex: number) {
        const lastIndex = this.htmlTabElements.length - 1;
        let nextIndex = tabIndex;
        switch (key) {
            case KEYBOARD_KEYS.LEFT_ARROW:
            case KEYBOARD_KEYS.UP_ARROW:
                nextIndex -= 1;
                break;
            case KEYBOARD_KEYS.RIGHT_ARROW:
            case KEYBOARD_KEYS.DOWN_ARROW:
                nextIndex += 1;
                break;
            case KEYBOARD_KEYS.HOME:
                nextIndex = 0;
                break;
            case KEYBOARD_KEYS.END:
                nextIndex = lastIndex;
                break;
            default:
                break;
        }
        if (nextIndex < 0) {
            nextIndex = lastIndex;
        }
        if (nextIndex > lastIndex) {
            nextIndex = 0;
        }
        this.focusTab(nextIndex);
    }

    focusTab(index: number) {
        if (
            this.htmlTabElements &&
            this.htmlTabElements.length - 1 >= index &&
            this.htmlTabElements[index] !== undefined
        ) {
            this.htmlTabElements[index].focus();
        }
    }

    isSelected = (tab: TabData) => {
        const { isTabSelected } = this.props;
        if (isTabSelected) {
            return isTabSelected(tab.id);
        }
        return false;
    };

    metricContext = (tab: TabData) => {
        const { getMetricContext } = this.props;
        if (getMetricContext) {
            return getMetricContext(tab);
        }
        return '';
    };

    appendDisclaimers = (tabProps: TabProps, tab: TabData) => {
        if (tab.disclaimers && tab.disclaimers.length > 0) {
            tabProps.conditionalDisclaimers = tab.disclaimers;
        }
        return tabProps;
    };

    appendAccessibility = (tabProps: TabProps, tab: TabData) => {
        const { getAccessibility } = this.props;
        if (getAccessibility) {
            tabProps.accessibility = getAccessibility(tab);
        }
        return tabProps;
    };

    render() {
        const {
            region,
            setTabActive,
            isTabSelected,
            getMetricContext,
            getAccessibility,
            filterCallback,
            superscriptScope,
            theme,
            tabThemeOverride,
            children,
            ...props
        } = this.props;

        const { workingTabs } = this;

        if (!workingTabs || workingTabs.length <= 0) {
            return null;
        }

        return (
            <TabsWrapper role="tablist" {...theme.TabsWrapper} {...props}>
                {workingTabs.map((tab: TabData, tabIndex: number) => {
                    let tabProps: TabProps = {
                        region,
                        superscriptScope,
                        id: tab.id,
                        isSelected: this.isSelected(tab),
                        onClick: e => this.onClick(e, tab),
                        onKeyDown: e => this.onKeyDown(e, tab, tabIndex),
                        metricContext: this.metricContext(tab),
                        themeOverride: tabThemeOverride,
                        getTabReference: (instance: HTMLElement) => {
                            this.saveTabReference(instance, tabIndex);
                        },
                    };
                    tabProps = this.appendDisclaimers(tabProps, tab);
                    tabProps = this.appendAccessibility(tabProps, tab);

                    return (
                        <Tab key={tab.id} {...tabProps}>
                            {tab.label}
                        </Tab>
                    );
                })}
            </TabsWrapper>
        );
    }
}

export default withTheme(Tabs) as any;



// WEBPACK FOOTER //
// ./src/Components/Tabs.tsx